import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import type { LegalDocument } from '../data/legal';
interface LegalModalProps {
  document: LegalDocument;
  onClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function LegalModal({
  document: doc,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: EASE
  }} className="fixed inset-0 z-50 flex items-end justify-center bg-ink/45 p-0 backdrop-blur-sm sm:items-center sm:p-6" onClick={onClose} role="dialog" aria-modal="true" aria-label={doc.title} data-id="element-174" data-mp="1d0w0ja0spra">
      <motion.div initial={{
      opacity: 0,
      y: 24,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 16,
      scale: 0.98
    }} transition={{
      duration: 0.26,
      ease: EASE
    }} onClick={e => e.stopPropagation()} className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[1.5rem] bg-ivory shadow-lift sm:max-h-[86vh] sm:rounded-[1.5rem]" data-id="element-175" data-mp="1foi1ce0g6d72">
        <div className="flex items-start justify-between gap-4 border-b border-gold/25 bg-cream px-6 py-5 sm:px-9" data-id="element-176" data-mp="wzbdkcksqvee">
          <h2 className="font-serif text-2xl font-light text-ink sm:text-[1.75rem]" data-id="element-177" data-mp="11wa5cogzmq9f">
            {doc.title}
          </h2>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-gold/35 text-ink-soft transition-colors duration-200 hover:border-gold hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-dark" data-id="element-178" data-mp="99pj22fdd4cq">
            <X className="h-4 w-4" strokeWidth={2} data-id="element-179" data-mp="fwmephcxrbiq" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-7 sm:px-9 sm:py-9" data-id="element-180" data-mp="ayb3b26zssye">
          <p className="text-[16.5px] leading-relaxed text-ink-soft" data-id="element-181" data-mp="3jeebra5xilns">
            {doc.intro}
          </p>

          <div className="mt-8 space-y-7" data-id="element-182" data-mp="3efjitfe8zgpy">
            {doc.sections.map(section => <section key={section.heading} data-id="element-183" data-mp="3tckbpm86dhq0">
                <h3 className="font-serif text-lg font-normal leading-snug text-ink sm:text-xl" data-id="element-184" data-mp="3tdbxk5bz0133">
                  {section.heading}
                </h3>
                <p className="mt-2.5 text-[16px] leading-[1.85] text-ink-soft" data-id="element-185" data-mp="18mbfbtlqh9jt">
                  {section.body}
                </p>
              </section>)}
          </div>

          <p className="mt-9 border-t border-gold/25 pt-6 text-[12.5px] italic text-ink-muted" data-id="element-186" data-mp="13pl018hr6hzg">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long'
          })}
          </p>
        </div>
      </motion.div>
    </motion.div>;
}